import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaPaperPlane, FaUser } from 'react-icons/fa';
import io from 'socket.io-client';

// --- Styled Components ---
const ChatContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 40px auto;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  background-color: #f7f7f7;
  /* Ensure ChatContainer itself doesn't cause page-level scroll */
  /* Remove any explicit height here if it causes overflow issues with parent */
`;

const ChatHeader = styled.div`
  background-color: #148f77;
  color: #fff;
  padding: 1rem;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
`;

const MessagesArea = styled.div`
  flex-grow: 1;
  padding: 1.25rem; /* 20px */
  overflow-y: auto;
  height: 400px; /* Fixed height is crucial for internal scrolling */
  background-color: #fff;
  scroll-behavior: smooth; /* Enable smooth scrolling for this specific area */
`;

const Message = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.9375rem; /* 15px */
  justify-content: ${({ sender }) => (sender === 'user' ? 'flex-end' : 'flex-start')};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    font-family: 'Tiro Bangla', serif;
  }
`;

const MessageBubble = styled.div`
  background-color: ${({ sender }) => (sender === 'user' ? '#e0f7fa' : '#f0f0f0')};
  color: #333;
  padding: 0.625rem 0.9375rem; /* 10px 15px */
  border-radius: 15px;
  max-width: 80%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ sender }) => (sender === 'user' ? '#0e6251' : '#6c757d')};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  margin: ${({ sender }) => (sender === 'user' ? '0 0 0 10px' : '0 10px 0 0')};
`;

const InputArea = styled.form`
  display: flex;
  padding: 0.9375rem; /* 15px */
  border-top: 1px solid #eee;
  background-color: #f0f0f0;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
`;

const ChatInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem 0.9375rem; /* 12px 15px */
  border: 1px solid #ccc;
  border-radius: 20px;
  outline: none;
  font-size: 1rem;
  margin-right: 0.625rem; /* 10px */

  &:focus {
    border-color: #148f77;
  }
  &:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background-color: #28a745;
  color: #fff;
  border: none;
  border-radius: 50%;
  margin-top:10px;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #218838;
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

// --- Styled Components for Name/Phone Input within Chat ---
const NamePhoneFormInChat = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.625rem; /* 10px */
  padding: 0.9375rem; /* 15px */
  background-color: #e0f7fa;
  border-radius: 10px;
  margin-bottom: 1.25rem; /* 20px */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const NamePhoneInputInChat = styled.input`
  padding: 0.625rem 0.75rem; /* 10px 12px */
  border: 1px solid #b2dfdb;
  border-radius: 8px;
  outline: none;
  font-size: 0.95rem;

  &:focus {
    border-color: #00796b;
  }
`;

const NamePhoneSubmitButtonInChat = styled.button`
  background-color: #00796b;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.625rem 0.9375rem; /* 10px 15px */
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #004d40;
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const FormTitleInChat = styled.h3`
  color: #004d40;
  margin-bottom: 0.625rem; /* 10px */
  text-align: center;
  font-size: 1.1rem;
`;

// --- Node.js API URL ---
const API_URL = 'https://chat-server-770204194118.asia-south1.run.app/';

function ChatScreen({ productName }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [isUserInfoCollected, setIsUserInfoCollected] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const messagesAreaRef = useRef(null); // Ref for the scrollable messages area
  const socketRef = useRef(null);
  const namePhoneIdRef = useRef(null);

  // --- Effect to load namephoneId from localStorage on component mount ---
  useEffect(() => {
    console.log("[Client] Attempting to load namephoneId...");
    
    let storedNamePhoneId = localStorage.getItem('chat_client_id');
    let name = '';
    let phone = '';

    if (storedNamePhoneId) {
      const parts = storedNamePhoneId.split('-');
      if (parts.length === 2) { 
        name = parts[0];
        phone = parts[1];
        setUserName(name);
        setUserPhone(phone);
        namePhoneIdRef.current = storedNamePhoneId;
        setIsUserInfoCollected(true);
        console.log("[Client] Existing namephoneId loaded:", storedNamePhoneId);
      } else {
        console.log("[Client] Existing ID is in invalid format, new ID required.");
        setIsUserInfoCollected(false);
      }
    } else {
      setIsUserInfoCollected(false);
      console.log("[Client] No namephoneId available, showing input form.");
    }
    setIsLoaded(true);
  }, []);

  // --- Effect for Socket.IO connection and event handling ---
  useEffect(() => {
    if (!isLoaded || !isUserInfoCollected || !namePhoneIdRef.current) {
      console.log("[Client] Not connecting Socket.IO: Loading not complete, user info not collected, or namephoneId not available.");
      return; 
    }

    // If socket already exists and is connected, and the namePhoneId hasn't changed,
    // prevent re-connecting unnecessarily.
    if (socketRef.current && socketRef.current.connected) {
        console.log("[Client] Socket.IO already connected. Skipping reconnect.");
        return;
    }

    console.log("[Client] Attempting Socket.IO connection...");
    socketRef.current = io(API_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketRef.current.on('connect', () => {
      console.log('[Client] Connected to Socket.IO server.');
      console.log('[Client] Emitting "join" event:', { client_id: namePhoneIdRef.current });
      socketRef.current.emit('join', { client_id: namePhoneIdRef.current });
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log(`[Client] Reconnected after ${attemptNumber} attempts.`);
      if (namePhoneIdRef.current) {
        socketRef.current.emit('join', { client_id: namePhoneIdRef.current });
      }
    });

    socketRef.current.on('new_message', (receivedMessages) => {
      console.log('[Client] Received "new_message" event:', receivedMessages);
      setMessages(receivedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
      console.log('[Client] Message state updated. Total messages:', receivedMessages.length);
    });

    socketRef.current.on('error', (data) => {
      console.error('[Client] Socket error:', data);
      console.log(`Socket error: ${data.error || 'Unknown error'}`);
    });

    socketRef.current.on('disconnect', () => {
      console.log('[Client] Disconnected from Socket.IO server.');
    });

    socketRef.current.on('reconnect_error', (error) => {
      console.error('[Client] Reconnect error:', error);
    });

    socketRef.current.on('reconnect_failed', () => {
      console.error('[Client] Reconnect failed after multiple attempts.');
    });

    return () => {
      if (socketRef.current) {
        console.log('[Client] Component unmounting, disconnecting Socket.IO.');
        socketRef.current.disconnect();
        socketRef.current = null; // Clear the ref to prevent stale connections
      }
    };
  }, [isLoaded, isUserInfoCollected, namePhoneIdRef.current]);

  // --- Effect for adding initial automated message ---
  useEffect(() => {
    // Add initial message only if loaded, user info collected, and no existing messages
    if (isLoaded && isUserInfoCollected && messages.length === 0) {
      console.log("[Client] Adding initial automated message...");
      setMessages([
        { 
          id: 'auto-msg-1', 
          sender: 'bot', 
          text: `👋 আমি আপনার অনলাইন শপ অ্যাসিস্ট্যান্ট। ${productName ? productName + ' সম্পর্কে' : ''} আপনার যেকোনো প্রশ্ন থাকলে জিজ্ঞাসা করতে পারেন।`, 
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [isLoaded, isUserInfoCollected, messages.length, productName]);

  // --- Effect for auto-scrolling to the latest message ---
  useEffect(() => {
    // Only scroll if the messages area exists and its content height is greater than its visible height
    if (messagesAreaRef.current) {
      const { scrollHeight, clientHeight } = messagesAreaRef.current;
      if (scrollHeight > clientHeight) {
        messagesAreaRef.current.scrollTop = scrollHeight; // Set scrollTop directly
        console.log("[Client] Scrolled to message area end.");
      }
    }
  }, [messages]);

  // --- Handler for submitting name and phone number ---
  const handleNamePhoneSubmit = useCallback((e) => {
    e.preventDefault();
    const trimmedUserName = userName.trim();
    const trimmedUserPhone = userPhone.trim();

    if (trimmedUserName === '' || trimmedUserPhone === '') {
      console.log("[Client] Both name and phone number are required.");
      return;
    }

    const newNamePhoneId = `${trimmedUserName}-${trimmedUserPhone}`;
    
    try {
      localStorage.setItem('chat_client_id', newNamePhoneId); 
      namePhoneIdRef.current = newNamePhoneId;
      console.log("[Client] New namephoneId saved to localStorage:", newNamePhoneId);
      setIsUserInfoCollected(true);

      // Disconnect existing socket to ensure the useEffect above re-runs
      // with the new namePhoneId and establishes a fresh connection.
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null; // Clear the ref
      }
      
      // Add the initial bot message immediately after successful submission
      setMessages([
        { 
          id: 'auto-msg-1', 
          sender: 'bot', 
          text: `👋 আমি আপনার অনলাইন শপ অ্যাসিস্ট্যান্ট। ${productName ? productName + ' সম্পর্কে' : ''} আপনার যেকোনো প্রশ্ন থাকলে জিজ্ঞাসা করতে পারেন।`, 
          timestamp: new Date().toISOString(),
        },
      ]);

    } catch (error) {
      console.error("[Client] Failed to save name and phone number:", error);
    }
  }, [userName, userPhone, productName]);

  // --- Handler for sending chat messages ---
  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault(); 
    const trimmedMessage = inputMessage.trim(); 
    if (trimmedMessage === '') {
      console.log("[Client] Cannot send empty message.");
      return; 
    }

    console.log('[Client] Attempting to send message:', trimmedMessage);
    if (socketRef.current && isUserInfoCollected && socketRef.current.connected) { 
      console.log('[Client] Emitting "send_message_ws" event:', {
        sender: 'user',
        text: trimmedMessage,
        productName: productName,
        client_id: namePhoneIdRef.current, 
        user_name: userName, 
        user_phone: userPhone, 
      });
      socketRef.current.emit('send_message_ws', {
        sender: 'user',
        text: trimmedMessage,
        productName: productName,
        client_id: namePhoneIdRef.current, 
        user_name: userName,
        user_phone: userPhone,
      });
    } else {
        console.error("[Client] Socket.IO connection not available/connected or user info not collected, message not sent.");
    }

    setInputMessage(''); 

  }, [inputMessage, productName, userName, userPhone, isUserInfoCollected]); 

  // --- UI Render ---
  if (!isLoaded) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>লোডিং...</div>;
  }

  return (
    <ChatContainer>
      <ChatHeader>
        চ্যাট সাপোর্ট
      </ChatHeader>
      <MessagesArea ref={messagesAreaRef}> {/* Attach ref here */}
        {/* Render name/phone input form if info is not collected */}
        {!isUserInfoCollected && (
          <NamePhoneFormInChat onSubmit={handleNamePhoneSubmit}>
            <FormTitleInChat>আপনার তথ্য দিন</FormTitleInChat>
            <NamePhoneInputInChat
              type="text"
              placeholder="আপনার নাম"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
              
            />
            <NamePhoneInputInChat
              type="tel"
              placeholder="আপনার ফোন নম্বর"
              value={userPhone}
              onChange={(e) => setUserPhone(e.target.value)}
              required
            />
            <NamePhoneSubmitButtonInChat 
              type="submit" 
              disabled={userName.trim() === '' || userPhone.trim() === ''}
            >
              শুরু করুন
            </NamePhoneSubmitButtonInChat>
          </NamePhoneFormInChat>
        )}

        {/* Render chat messages */}
        {messages.map((msg) => (
          <Message key={msg.id} sender={msg.sender}>
            {msg.sender === 'bot' ? (
                <Avatar sender="bot">A</Avatar> 
            ) : (
                <Avatar sender="user"><FaUser /></Avatar>
            )}
            <MessageBubble sender={msg.sender}>{msg.text}</MessageBubble>
          </Message>
        ))}
        {/* You no longer need a separate div for scrolling if you directly manipulate MessagesAreaRef */}
      </MessagesArea>
      <InputArea onSubmit={handleSendMessage}>
        <ChatInput
          type="text"
          placeholder={isUserInfoCollected ? "আপনার প্রশ্ন টাইপ করুন..." : "মেসেজ পাঠানোর জন্য আপনার তথ্য দিন..."}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          disabled={!isUserInfoCollected}
        />
        <SendButton type="submit" disabled={inputMessage.trim() === '' || !isUserInfoCollected}>
          <FaPaperPlane />
        </SendButton>
      </InputArea>
    </ChatContainer>
  );
}

export default ChatScreen;